import { defineStore } from 'pinia'
import { useCookie } from '#app'

interface OnboardingState {
  completedSteps: number[]
  totalSteps: number
}

const COOKIE_KEY = 'onboarding-progress'

export const useOnboardingStore = defineStore('onboarding', {
  state: (): OnboardingState => ({
    completedSteps: [],
    totalSteps: 10,
  }),

  getters: {
    progress: (state): number => {
      return Math.min(Math.round((state.completedSteps.length / state.totalSteps) * 100), 100)
    },
    isStepCompleted:
      (state) =>
      (index: number): boolean => {
        return state.completedSteps.includes(index)
      },
    allStepsCompleted: (state): boolean => {
      return (
        state.completedSteps.length >= state.totalSteps ||
        Math.round((state.completedSteps.length / state.totalSteps) * 100) >= 100
      )
    },
  },

  actions: {
    loadFromCookie() {
      const savedProgress = useCookie(COOKIE_KEY)
      if (!savedProgress.value) return

      try {
        const value = savedProgress.value
        const parsed = Array.isArray(value) ? value : JSON.parse(value)
        if (Array.isArray(parsed)) {
          this.completedSteps = parsed.filter((n) => typeof n === 'number')
        }
      } catch (e) {
        logError('Failed to parse onboarding progress:', e)
      }
    },

    completeStep(index: number) {
      if (index < 0 || index >= this.totalSteps) {
        warn('Invalid step index:', index)
        return
      }

      if (!this.completedSteps.includes(index)) {
        this.completedSteps.push(index)
        this.completedSteps.sort((a, b) => a - b)
        // Save to cookie
        const cookie = useCookie(COOKIE_KEY)
        cookie.value = JSON.stringify(this.completedSteps)
      }
    },

    reset() {
      this.completedSteps = []
      const cookie = useCookie(COOKIE_KEY)
      cookie.value = JSON.stringify([])
    },

    setTotalSteps(total: number) {
      if (total > 0) {
        this.totalSteps = total
      }
    },
  },
})
